// User Stories — review + send. Stories are written by the dashboard, not by
// hand: open a user interview on the Interviews tab and click "Generate user
// stories" (one draft per feature request, linked back to the interview). This
// tab is where you review those drafts, tweak if needed, and send each to tasx
// as a checklist step on the standing "Convert evidence into user stories" task.
// A "New user story" button is here too for the occasional ad-hoc story.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useUserStories,
  useUpsertUserStory,
  useSendUserStory,
  useInterviews,
  type UserStoryRow,
  type UserStoryInput,
  type UserStoryStatus,
  type StorySeverity,
  type InterviewListRow,
} from '../lib/queries';

const PRIORITY_OPTS: StorySeverity[] = ['high', 'medium', 'low'];
const STATUS_OPTS: UserStoryStatus[] = ['draft', 'submitted', 'accepted'];

function humanize(s: string | null | undefined): string {
  if (!s) return '—';
  return s.replace(/_/g, ' ');
}

function fmtDate(d: string | null): string {
  if (!d) return '—';
  return new Date(`${d}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function currentSunday(): string {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  d.setDate(d.getDate() - d.getDay());
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function newStoryId(): string {
  return `us_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

function storyLine(r: UserStoryRow): string {
  if (r.title && r.title.trim()) return r.title;
  return `As a ${r.as_a || 'user'}, I want ${r.i_want}`;
}

function interviewLabel(iv: InterviewListRow): string {
  const who = iv.title || iv.counterpart || iv.interview_id;
  return iv.date ? `${who} · ${fmtDate(iv.date)}` : who;
}

export function UserStories() {
  const { data, isLoading } = useUserStories();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<UserStoryRow | null>(null);
  const send = useSendUserStory();
  const rows = data ?? [];

  const onAdd = () => { setEditing(null); setShowForm(true); };
  const onEdit = (r: UserStoryRow) => { setEditing(r); setShowForm(true); };
  const onDone = () => { setShowForm(false); setEditing(null); };

  const sendingId = send.isPending ? send.variables : null;

  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section H</span>
          <span>User Stories</span>
          <span>{rows.length} total</span>
        </div>
        <h1 className="page__title">User Stories</h1>
        <p className="page__deck">
          The dashboard writes these from your interviews: open a user interview and click
          “Generate user stories.” Review the drafts here, then send each to tasx as a
          checklist item on the user-stories task. Accepting one checks it off there.
        </p>
      </header>

      {!showForm && (
        <div className="podcast__actions">
          <button type="button" className="podcast__action-btn podcast__action-btn--primary" onClick={onAdd}>
            New user story
          </button>
        </div>
      )}

      {showForm && <UserStoryForm initial={editing} defaultWeek={currentSunday()} onDone={onDone} />}

      {isLoading && <p><em>Loading…</em></p>}

      {!isLoading && rows.length === 0 && (
        <div className="podcast__empty">
          No user stories yet. Open a user interview on the Interviews tab and click
          “Generate user stories.”
        </div>
      )}

      {rows.length > 0 && (
        <table className="interview__table">
          <thead>
            <tr>
              <th>Week</th>
              <th>Story</th>
              <th>Priority</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.story_id} className="podcast__row">
                <td className="podcast__when">{fmtDate(r.week_of)}</td>
                <td className="podcast__topic">
                  {storyLine(r)}
                  {r.tasx_step_id && <span className="painpoint__badge painpoint__badge--tasx">in tasx</span>}
                  {r.source_interview_id && (
                    <Link
                      to={`/interviews?tab=interviews&id=${encodeURIComponent(r.source_interview_id)}`}
                      className="userstory__source-link"
                    >
                      from interview →
                    </Link>
                  )}
                </td>
                <td>
                  {r.priority
                    ? <span className={`painpoint__badge painpoint__badge--sev-${r.priority}`}>{r.priority}</span>
                    : <span className="podcast__muted">—</span>}
                </td>
                <td>
                  <span className={`painpoint__badge painpoint__badge--st-${r.status}`}>{humanize(r.status)}</span>
                </td>
                <td className="userstory__actions">
                  <button type="button" className="podcast__detail-btn" onClick={() => onEdit(r)}>edit</button>
                  <button
                    type="button"
                    className="podcast__action-btn podcast__action-btn--primary userstory__send"
                    disabled={send.isPending}
                    onClick={() => send.mutate(r.story_id)}
                  >
                    {sendingId === r.story_id ? 'Sending…' : r.tasx_step_id ? 'Re-sync' : 'Send to tasx'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {send.isError && <p className="painpoint__form-error">Could not send to tasx. {send.error?.message}</p>}
    </div>
  );
}

function UserStoryForm({ initial, defaultWeek, onDone }: { initial: UserStoryRow | null; defaultWeek: string; onDone: () => void }) {
  const upsert = useUpsertUserStory();
  const { data: interviews } = useInterviews();
  const userInterviews = (interviews ?? []).filter((iv) => iv.interview_type === 'user');

  const [asA, setAsA] = useState(initial?.as_a ?? '');
  const [iWant, setIWant] = useState(initial?.i_want ?? '');
  const [soThat, setSoThat] = useState(initial?.so_that ?? '');
  const [acceptance, setAcceptance] = useState(initial?.acceptance ?? '');
  const [priority, setPriority] = useState<string>(initial?.priority ?? '');
  const [source, setSource] = useState(initial?.source ?? '');
  const [sourceInterviewId, setSourceInterviewId] = useState(initial?.source_interview_id ?? '');
  const [weekOf, setWeekOf] = useState(initial?.week_of ?? defaultWeek);
  const [status, setStatus] = useState<UserStoryStatus>(initial?.status ?? 'draft');

  const pickInterview = (id: string) => {
    setSourceInterviewId(id);
    const iv = userInterviews.find((x) => x.interview_id === id);
    if (iv && !source.trim()) setSource(interviewLabel(iv));
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!iWant.trim()) return;
    const input: UserStoryInput = {
      story_id: initial?.story_id ?? newStoryId(),
      week_of: weekOf || null,
      as_a: asA.trim() || null,
      i_want: iWant.trim(),
      so_that: soThat.trim() || null,
      acceptance: acceptance.trim() || null,
      priority: (priority || null) as StorySeverity | null,
      source: source.trim() || null,
      source_interview_id: sourceInterviewId || null,
      status,
    };
    upsert.mutate(input, { onSuccess: () => onDone() });
  };

  return (
    <form className="painpoint__form" onSubmit={submit}>
      <h2 className="podcast__section-title">{initial ? 'Edit user story' : 'New user story'}</h2>
      <div className="painpoint__form-grid">
        <label className="painpoint__field painpoint__field--full">
          <span>From interview</span>
          <select className="filter__select" value={sourceInterviewId} onChange={(e) => pickInterview(e.target.value)}>
            <option value="">— none —</option>
            {userInterviews.map((iv) => (
              <option key={iv.interview_id} value={iv.interview_id}>{interviewLabel(iv)}</option>
            ))}
          </select>
        </label>

        <label className="painpoint__field">
          <span>As a…</span>
          <input className="filter__input" value={asA} onChange={(e) => setAsA(e.target.value)} placeholder="role (e.g. new user)" />
        </label>
        <label className="painpoint__field painpoint__field--full">
          <span>I want… *</span>
          <input className="filter__input" value={iWant} onChange={(e) => setIWant(e.target.value)} placeholder="the goal / capability" required />
        </label>
        <label className="painpoint__field painpoint__field--full">
          <span>so that…</span>
          <input className="filter__input" value={soThat} onChange={(e) => setSoThat(e.target.value)} placeholder="the benefit" />
        </label>

        <label className="painpoint__field">
          <span>Priority</span>
          <select className="filter__select" value={priority} onChange={(e) => setPriority(e.target.value)}>
            <option value="">—</option>
            {PRIORITY_OPTS.map((p) => <option key={p} value={p}>{humanize(p)}</option>)}
          </select>
        </label>
        <label className="painpoint__field">
          <span>Week of</span>
          <input className="filter__input" type="date" value={weekOf} onChange={(e) => setWeekOf(e.target.value)} />
        </label>
        <label className="painpoint__field">
          <span>Source</span>
          <input className="filter__input" value={source} onChange={(e) => setSource(e.target.value)} placeholder="interview / evidence" />
        </label>
        {initial && (
          <label className="painpoint__field">
            <span>Status</span>
            <select className="filter__select" value={status} onChange={(e) => setStatus(e.target.value as UserStoryStatus)}>
              {STATUS_OPTS.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
            </select>
          </label>
        )}

        <label className="painpoint__field painpoint__field--full">
          <span>Acceptance criteria</span>
          <textarea className="filter__input painpoint__textarea" value={acceptance} onChange={(e) => setAcceptance(e.target.value)} rows={2} placeholder="What does done look like?" />
        </label>
      </div>

      {upsert.isError && <p className="painpoint__form-error">Could not save. {upsert.error?.message}</p>}

      <div className="podcast__actions">
        <button type="submit" className="podcast__action-btn podcast__action-btn--primary" disabled={upsert.isPending || !iWant.trim()}>
          {upsert.isPending ? 'Saving…' : initial ? 'Save changes' : 'Save draft'}
        </button>
        <button type="button" className="podcast__action-btn" onClick={onDone}>Cancel</button>
      </div>
      <p className="userstory__hint">Saving keeps a draft. Use “Send to tasx” on the row to submit it.</p>
    </form>
  );
}
